import { capitalize } from 'utils';
import { i18n } from 'i18n';
import { trackEvent } from 'providers/analytics';
import {
  AddApprovalPresetGroupEmailForm,
  AddApproverCommand,
  ApprovalPresetGroupDto,
  AutoCreatedPourDto,
  BinderSavingsReportDto,
  BulkDeletePoursRequest,
  CreateApprovalPresetGroupForm,
  CreatePourPresetForm,
  CreatePourPresetGroupForm,
  FileParameter,
  FileResponse,
  PagedListOfApprovalPresetGroupDto,
  PagedListOfPourPresetGroupDto,
  PagedListOfPourReportDto,
  PagedListOfStopePoursDto,
  PagerParameters, PourBreakingScheduleDetailsDto, PourDataForm, PourDto,
  PourForm,
  PourPresetDto,
  PourPresetGroupDto,
  PourProjectPreferencesDto,
  PourRouteImageDto,
  PoursSearchParameters,
  PourVolumeComparisonDto,
  PublicPourApprovalViewDto,
  RemoveApproverCommand,
  ReorderPourPresetsForm,
  ResendPourNoteApprovalRequestCommand,
  StopeBackfillSummaryDto,
  UnapprovePourNoteApprovalCommand,
  UpdatePourDateCommand,
  UpdatePourPresetForm,
  UpdatePourReferenceCommand,
  UpdatePourVolumeCommand,
  UscStrengthRecordDto
} from './api';
import {
  makeCreateMutation, makeDeleteMutation, makeGetQuery,
  makeListQuery,
  makeUpdateMutation,
  PageParams
} from './useApi';

export const POURS_KEY = 'pours';
const POUR_KEY = 'pour';
const PAGED_POURS_KEY = 'paged_pours';
const STOPE_BACKFILL_KEY = 'stopeBackfill';
const POUR_PRESET_GROUPS_KEY = 'pour_preset_groups';
const POUR_PRESET_GROUP_KEY = 'pour_preset_group';
const POUR_PRESET_KEY = 'pour_preset';
const POUR_PRESETS_KEY = 'pour_presets';
const APPROVAL_PRESET_GROUPS_KEY = 'approval_preset_groups';
const APPROVAL_PRESET_GROUP_KEY = 'approval_preset_group';
const PUBLIC_POUR_KEY = 'public_pour';
const UCS_STRENGTH_GRAPH_KEY = 'ucs_strength_group';
export const STOPE_BACKFILLS_KEY = 'stopeBackfills';
export const POUR_NOTE_KEY = 'pour-note';
export const ROUTE_IMAGES_KEY = 'pour-route-images';

export interface PourMutation {
  stopeId: string;
  recipeId: string;
  recipeReference: string;
  data: PourForm;
}
// Pours inherit scenarioId transitively from their source recipe — CreatePourCommandHandler reads recipe.ScenarioId server-side,
// so this endpoint deliberately takes no scenarioId parameter.
export const useCreatePourMutation = makeCreateMutation<string, PourMutation>(
  POURS_KEY,
  (api) => (mutation) => api.pours.create(mutation.recipeId, mutation.data).then((pourId) => {
    trackEvent('pour_created', {
      pour_id: pourId,
      stope: mutation.stopeId,
      volume_m3: mutation.data.volume,
    });
    return pourId;
  }),
  (mutation) => i18n._('A pour was created from the recipe referenced').concat(` ${mutation.recipeReference}.`),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY],
);

export const useDownloadPourNote = makeGetQuery<FileResponse, { pourId: string, pourNoteId: string }>(
  POUR_NOTE_KEY,
  (api, { pourId, pourNoteId }) => () => api.pours.downloadPourNote(pourId, pourNoteId),
);

export const useUpdatePourDateMutation = makeUpdateMutation<void, UpdatePourDateCommand>(
  POUR_KEY,
  POURS_KEY,
  (api, pourId) => (mutation) => api.pours.updatePourDate(pourId, mutation),
  () => i18n._('The pour date has been successfully updated.'),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY, POUR_NOTE_KEY],
);

export const useUpdatePourVolumeMutation = makeUpdateMutation<void, UpdatePourVolumeCommand>(
  POUR_KEY,
  POURS_KEY,
  (api, pourId) => (mutation) => api.pours.updatePourVolume(pourId, mutation),
  () => i18n._('The pour volume has been successfully updated.'),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY, POUR_NOTE_KEY],
);

export interface PoursQueryParams {
  projectId: string;
  pourId?: string;
  stopeId: string;
  scenarioId?: string;
  // Rock-fill pours are excluded from pour queries everywhere except the stope-fill view, which opts in.
  includeRockFill?: boolean;
}

export const usePoursQuery = makeListQuery<PoursQueryParams, PourDto[]>(
  POURS_KEY,
  (api, params) => () => api.pours.getAll(params.projectId, params.stopeId, params.scenarioId, params.includeRockFill),
);

export interface ProjectPoursQueryParams extends PagerParameters, PoursSearchParameters {
  projectId: string;
  scenarioId?: string;
}

export const useProjectPoursQuery = makeListQuery<ProjectPoursQueryParams, PagedListOfPourReportDto>(
  PAGED_POURS_KEY,
  (api, params) => () => api.pours.getAllByProject(params.projectId, params.page, params.pageSize,
    params.startDate?.toJSON(), params.endDate?.toJSON(), params.status, params.stopeId, params.scenarioId),
);

export const usePourQuery = makeGetQuery<PourDto, string>(
  POUR_KEY,
  (api, entityId) => () => api.pours.get(entityId),
);

export interface MineOverviewQueryParams {
  mineModelId: string;
  scenarioId?: string;
}

export const useMineOverviewQuery = makeListQuery<MineOverviewQueryParams, StopeBackfillSummaryDto[]>(
  STOPE_BACKFILLS_KEY,
  (api, params) => () => api.pours.getMineOverview(params.mineModelId, params.scenarioId),
);

export interface StopeOverviewParams {
  projectId: string;
  stopeId: string;
  scenarioId?: string;
}

export const useStopeOverviewQuery = makeGetQuery<StopeBackfillSummaryDto, StopeOverviewParams>(
  STOPE_BACKFILL_KEY,
  (api, params) => () => api.pours.getStopeOverview(params.projectId, params.stopeId, params.scenarioId),
);

interface PourNoteMutation {
  pourReference: string;
  pourId: string;
  pourNoteId?: string;
  isPdf?: boolean;
  includeRoutePoints: boolean;
  // Only the create endpoint takes this - approve reads the flag persisted on the note - and this
  // interface is shared by both, so it is optional rather than required.
  includeRiseRate?: boolean;
  routePoints: string;
  hglImageBase64: string;
  // Data uri of the rise-rate chart; empty when the stope geometry has not loaded or no pour carries a
  // recipe flow rate, in which case the note omits the section.
  riseRateImageBase64: string;
}

export const useApprovePourNoteMutation = makeUpdateMutation<void, PourNoteMutation>(
  POUR_KEY,
  POURS_KEY,
  (api) => (mutation) => api.pours.approvePourNote(
    mutation.pourId,
    mutation.pourNoteId ?? '',
    mutation.isPdf,
    mutation.includeRoutePoints,
    mutation.routePoints,
    mutation.hglImageBase64,
    mutation.riseRateImageBase64,
  ),
  (mutation) => i18n._('The stope note has been approved for the pour referenced').concat(` ${mutation.pourReference}.`),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY],
);

export const useCreatePourNoteQuery = makeUpdateMutation<void, PourNoteMutation>(
  POUR_KEY,
  POURS_KEY,
  (api) => (mutation) => api.pours.createPourNote(
    mutation.pourId,
    mutation.isPdf,
    mutation.includeRoutePoints,
    mutation.includeRiseRate,
    mutation.routePoints,
    mutation.hglImageBase64,
    mutation.riseRateImageBase64,
  ),
  (mutation) => i18n._('A stope note has been generated for the pour referenced').concat(` ${mutation.pourReference}.`),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY],
);

interface PourNoteParams {
  pourId: string;
  pourNoteId: string;
}

const POUR_FILE = 'pour_file';
export const usePourNoteFileQuery = makeGetQuery<FileResponse, PourNoteParams>(
  POUR_FILE,
  (api, params) => () => api.pours.downloadPourNote(params.pourId, params.pourNoteId),
);

// Route images attached to a pour and embedded into its stope note.
export const useRouteImagesQuery = makeListQuery<string, PourRouteImageDto[]>(
  ROUTE_IMAGES_KEY,
  (api, pourId) => () => api.pours.getRouteImages(pourId),
);

export interface AddRouteImagesMutation {
  images: FileParameter[];
}
export const useAddRouteImagesMutation = makeUpdateMutation<PourRouteImageDto[], AddRouteImagesMutation>(
  ROUTE_IMAGES_KEY,
  null,
  (api, pourId) => (mutation) => api.pours.addRouteImages(pourId, mutation.images),
  () => i18n._('Route images added to the stope note.'),
);

export interface DeleteRouteImageMutation {
  fileId: string;
}
export const useDeleteRouteImageMutation = makeUpdateMutation<void, DeleteRouteImageMutation>(
  ROUTE_IMAGES_KEY,
  null,
  (api, pourId) => (mutation) => api.pours.deleteRouteImage(pourId, mutation.fileId),
  () => i18n._('Route image removed.'),
);

export interface PourAddApproverMutation {
  pourId: string;
  pourNoteId: string;
  addApprover: AddApproverCommand;
}

export const useCreateApproverMutation = makeUpdateMutation<void, PourAddApproverMutation>(
  POUR_KEY,
  POURS_KEY,
  (api) => (mutation) => api.pours.addApproverToPourNote(mutation.pourId, mutation.pourNoteId, mutation.addApprover!),
  (mutation) => `${mutation.addApprover!.email} `.concat(i18n._('has been added from the approver list.')),
);

export interface PourRemoveApproverMutation {
  pourId: string;
  pourNoteId: string;
  removeApprover: RemoveApproverCommand;
}

export const useRemoveApproverMutation = makeUpdateMutation<void, PourRemoveApproverMutation>(
  POUR_KEY,
  POURS_KEY,
  (api) => (mutation) => api.pours.removeApproverToPourNote(mutation.pourId, mutation.pourNoteId, mutation.removeApprover!),
  (mutation) => `${mutation.removeApprover!.email} `.concat(i18n._('has been removed from the approver list.')),
);

export const useSendForApprovalMutation = makeUpdateMutation<void, PourNoteParams>(
  POUR_KEY,
  POURS_KEY,
  (api) => (mutation) => api.pours.requestPourNoteApproval(mutation.pourId, mutation.pourNoteId),
  () => i18n._('Stope Note sent for approval.'),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY],
);

export interface ApproverApprovePourNoteMutation {
  pourId: string;
  pourNoteId: string;
  email: string;
  signature?: FileParameter;
}

export const useApproverApprovePourNoteMutation = makeUpdateMutation<void, ApproverApprovePourNoteMutation>(
  POUR_KEY,
  POURS_KEY,
  (api) => (mutation) => api.pours.approvePourNoteApproval(mutation.pourId, mutation.pourNoteId, mutation.email, mutation.signature),
  (mutation) => i18n._('The pour was successfully approved for').concat(` ${mutation.email}.`),
);

export interface ApproverUnapprovePourNoteMutation {
  pourId: string;
  pourNoteId: string;
  command: UnapprovePourNoteApprovalCommand,
}

export const useApproverUnapprovePourNoteMutation = makeUpdateMutation<void, ApproverUnapprovePourNoteMutation>(
  POUR_KEY,
  POURS_KEY,
  (api) => (mutation) => api.pours.unapprovePourNoteApproval(mutation.pourId, mutation.pourNoteId, mutation.command),
  (mutation) => i18n._('The pour was successfully un-approved for').concat(` ${mutation.command.email}.`),
);

export interface ResendPourNoteApprovalMutation {
  pourId: string;
  pourNoteId: string;
  form: ResendPourNoteApprovalRequestCommand;
}

export const useResendPourNoteApprovalMutation = makeUpdateMutation<void, ResendPourNoteApprovalMutation>(
  POUR_KEY,
  POURS_KEY,
  (api) => (mutation) => api.pours.resendPourNoteApproval(mutation.pourId, mutation.pourNoteId, mutation.form),
  () => i18n._('Emails for stope note approvals successfully resent.'),
);

export interface PourDataMutation {
  pourId: string;
  data: PourDataForm;
  operatorLogNoteFile?: FileParameter;
}

export const useCreatePourDataMutation = makeUpdateMutation<void, PourDataMutation>(
  POUR_KEY,
  POURS_KEY,
  (api) => (mutation) => api.pours.completePour(
    mutation.pourId,
    mutation.operatorLogNoteFile,
    mutation.data.pourCompletedDate?.toJSON(),
    mutation.data.dryTonnage,
    mutation.data.massConcentration,
    mutation.data.binderContent,
    mutation.data.volume,
    mutation.data.totalBinder,
    mutation.data.comments,
    undefined,
    mutation.data.binderTailingsRatio,
    mutation.data.documents,
    mutation.data.ucsAverage,
  ).then((result) => {
    trackEvent('pour_completed', {
      pour_id: mutation.pourId,
      volume_m3: mutation.data.volume,
    });
    return result;
  }),
  () => i18n._('The completion data was successfully created.'),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY],
);

export const useUpdatePourDataMutation = makeUpdateMutation<void, PourDataMutation>(
  POUR_KEY,
  POURS_KEY,
  (api) => (mutation) => api.pours.updatePourData(
    mutation.pourId,
    mutation.operatorLogNoteFile,
    mutation.data.pourCompletedDate?.toJSON(),
    mutation.data.dryTonnage,
    mutation.data.massConcentration,
    mutation.data.binderContent,
    mutation.data.volume,
    mutation.data.totalBinder,
    mutation.data.comments,
    undefined,
    mutation.data.binderTailingsRatio,
    mutation.data.documents,
    mutation.data.ucsAverage,
  ),
  () => i18n._('The completion data was successfully created.'),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY],
);

export const useVoidPourNoteMutation = makeUpdateMutation<void, PourNoteParams>(
  POUR_KEY,
  POURS_KEY,
  (api) => (mutation) => api.pours.voidPourNote(mutation.pourId, mutation.pourNoteId),
  () => i18n._('The stope note has been successfully voided.'),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY],
);

const POUR_ATTACHMENT_KEY = 'pour-attachment';

export const useDownloadPourAttachment = makeGetQuery<FileResponse, { pourId: string, fileId: string }>(
  POUR_ATTACHMENT_KEY,
  (api, { pourId, fileId }) => () => api.pours.downloadAttachment(pourId, fileId),
);

export const useAbandonPourMutation = makeUpdateMutation<void, string>(
  POUR_KEY,
  POURS_KEY,
  (api) => (pourId) => api.pours.abandonPour(pourId),
  () => i18n._('The stope note has been successfully voided.'),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY],
);

export const useUnapprovePourNoteMutation = makeUpdateMutation<void, string>(
  POUR_KEY,
  POURS_KEY,
  (api, pourId) => (pourNoteId) => api.pours.unapprovePourNote(pourId, pourNoteId),
  () => i18n._('The stope note has been successfully voided.'),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY],
);

export interface GetBinderCostSavedByProjectQueryParameters {
  startDate: string;
  endDate: string;
  mineModelId: string;
  scenarioId?: string;
}

export const useGetBinderCostSavedByProjectQuery = makeListQuery<GetBinderCostSavedByProjectQueryParameters, BinderSavingsReportDto>(
  POURS_KEY,
  (api, params) => () => api.pours.getBinderCostSavedByProject(params.mineModelId, params.startDate, params.endDate, params.scenarioId),
);

export interface GetHistoricalPourVolumeDataByProjectQueryParameters {
  startDate?: string;
  endDate?: string;
  mineModelId: string;
  scenarioId?: string;
}

export const useGetHistoricalPourVolumeDataByProjectQuery = makeListQuery<GetHistoricalPourVolumeDataByProjectQueryParameters, PourVolumeComparisonDto[]>(
  POURS_KEY,
  (api, params) => () => api.pours.getHistoricalPourVolumeDataByProject(params.mineModelId, params.startDate, params.endDate, undefined, undefined, params.scenarioId),
);

export const useGetPourBreakingScheduleDetails = makeGetQuery<PourBreakingScheduleDetailsDto, { pourId: string }>(
  POUR_NOTE_KEY,
  (api, { pourId }) => () => api.pours.getPourBreakingScheduleDetails(pourId),
);

export const useGetProjectPreferencesForPour = makeGetQuery<PourProjectPreferencesDto, string>(
  POUR_NOTE_KEY,
  (api, pourId) => () => api.pours.getProjectPreferencesForPour(pourId),
);

export interface ApprovalPresetGroupMutation {
  projectId: string;
  form: CreateApprovalPresetGroupForm;
}

export const useCreateApprovalPresetGroup = makeCreateMutation<string, ApprovalPresetGroupMutation>(
  APPROVAL_PRESET_GROUPS_KEY,
  (api) => (mutation) => api.pours.createApprovalPresetGroup(mutation.projectId, mutation.form),
  () => i18n._('Approval preset group created'),
);

export interface ApprovalPresetGroupsQueryParams extends PageParams {
  projectId: string;
  groupName?: string;
}

export const useGetApprovalPresetGroupsQuery = makeListQuery<ApprovalPresetGroupsQueryParams, PagedListOfApprovalPresetGroupDto>(
  APPROVAL_PRESET_GROUPS_KEY,
  (api, params) => () => api.pours.getApprovalPresetGroups(params.projectId, params.page, params.pageSize, params.groupName),
);

export const useGetApprovalPresetGroupQuery = makeGetQuery<ApprovalPresetGroupDto, string>(
  APPROVAL_PRESET_GROUP_KEY,
  (api, approvalPresetGroupId) => () => api.pours.getApprovalPresetGroup(approvalPresetGroupId),
);

export interface UpdateApprovalPresetGroupNameMutation {
  approvalPresetGroupId: string;
  name: string;
}

export const useUpdateApprovalPresetGroupNameMutation = makeCreateMutation<void, UpdateApprovalPresetGroupNameMutation>(
  APPROVAL_PRESET_GROUPS_KEY,
  (api) => (mutation) => api.pours.updateApprovalPresetGroupName(mutation.approvalPresetGroupId, mutation.name),
  () => i18n._('The approval preset group has been updated.'),
  [APPROVAL_PRESET_GROUP_KEY],
);

export const useDeleteApprovalPresetGroupMutation = makeDeleteMutation<void, ApprovalPresetGroupDto>(
  APPROVAL_PRESET_GROUP_KEY,
  APPROVAL_PRESET_GROUPS_KEY,
  (api) => (group) => api.pours.deleteApprovalPresetGroup(group.entityId),
  (mutation) => i18n._('Approval preset group').concat(` ${capitalize(mutation.name ?? '')} `).concat(i18n._('successfully deleted')),
  (mutation) => mutation.entityId,
);

export interface AddApprovalPresetGroupEmailMutation {
  approvalPresetGroupId: string;
  form: AddApprovalPresetGroupEmailForm;
}

export const useAddApprovalPresetGroupEmailMutation = makeCreateMutation<void, AddApprovalPresetGroupEmailMutation>(
  APPROVAL_PRESET_GROUPS_KEY,
  (api) => (mutation) => api.pours.addApprovalPresetGroupEmail(mutation.approvalPresetGroupId, mutation.form),
  () => '',
  [APPROVAL_PRESET_GROUP_KEY],
);

export interface RemoveApprovalPresetGroupEmailMutation {
  approvalPresetGroupId: string;
  email: string;
}

export const useRemoveApprovalPresetGroupEmailMutation = makeCreateMutation<void, RemoveApprovalPresetGroupEmailMutation>(
  APPROVAL_PRESET_GROUPS_KEY,
  (api) => (mutation) => api.pours.removeApprovalPresetGroupEmail(mutation.approvalPresetGroupId, mutation.email),
  () => '',
  [APPROVAL_PRESET_GROUP_KEY],
);

export interface PourPresetGroupMutation {
  projectId: string;
  form: CreatePourPresetGroupForm;
}
export const useCreatePourPresetGroup = makeCreateMutation<string, PourPresetGroupMutation>(
  POUR_PRESET_GROUPS_KEY,
  (api) => (mutation) => api.pours.createPourPresetGroup(mutation.projectId, mutation.form),
  () => i18n._('Pour Preset Group created'),
);

export const useCreatePourPreset = makeCreateMutation<string, CreatePourPresetForm>(
  POUR_PRESETS_KEY,
  (api) => (form) => api.pours.createPourPreset(form),
  () => i18n._('Pour Preset created'),
);

export interface PourPresetGroupsQueryParams extends PageParams {
  projectId: string;
  pourPresetGroupName?: string;
}

export const useGetPourPresetGroupsQuery = makeListQuery<PourPresetGroupsQueryParams, PagedListOfPourPresetGroupDto>(
  POUR_PRESET_GROUPS_KEY,
  (api, params) => () => api.pours.getPourPresetGroups(params.projectId, params.page, params.pageSize, params.pourPresetGroupName),
);

export const useUpdatePourPresetGroupNameMutation = makeUpdateMutation<void, PourPresetGroupMutation>(
  POUR_PRESET_GROUPS_KEY,
  POUR_PRESET_GROUP_KEY,
  (api, pourPresetGroupId) => (params) => api.pours.updatePourPresetGroupName(pourPresetGroupId, params.form.name),
  () => i18n._('The pour preset group has been updated.'),
);

export const useDeletePourPresetGroupMutation = makeDeleteMutation<void, PourPresetGroupDto>(
  POUR_PRESET_GROUP_KEY,
  POUR_PRESET_GROUPS_KEY,
  (api) => (pourPresetGroup) => api.pours.deletePourPresetGroup(pourPresetGroup.entityId),
  (mutation) => i18n._('Pour preset group').concat(` ${capitalize(mutation.name ?? '')} `).concat(i18n._('successfully deleted')),
  (mutation) => mutation.entityId,
);

export const useRestorePourPresetGroupMutation = makeUpdateMutation<FileResponse, string>(
  POUR_PRESET_GROUP_KEY,
  POUR_PRESET_GROUPS_KEY,
  (api) => (pourPresetGroupId) => api.pours.restorePourPresetGroup(pourPresetGroupId),
  () => i18n._('Pour preset group successfully restored'),
  [POUR_PRESET_GROUP_KEY],
);

export const useGetPourPresetGroup = makeGetQuery<PourPresetGroupDto, string>(
  POUR_PRESET_GROUP_KEY,
  (api, pourPresetGroupId) => () => api.pours.getPourPresetGroup(pourPresetGroupId),
);

export const useGetPourPreset = makeGetQuery<PourPresetDto, string>(
  POUR_PRESET_KEY,
  (api, pourPresetId) => () => api.pours.getPourPreset(pourPresetId),
);

export const useUpdatePourPresetMutation = makeUpdateMutation<void, UpdatePourPresetForm>(
  POUR_PRESETS_KEY,
  POUR_PRESET_KEY,
  (api, pourPresetId) => (form) => api.pours.updatePourPreset(pourPresetId, form),
  () => i18n._('The pour preset has been updated.'),
);

export const useReorderPourPresetsMutation = makeUpdateMutation<void, ReorderPourPresetsForm>(
  POUR_PRESET_GROUPS_KEY,
  POUR_PRESET_GROUP_KEY,
  (api, pourPresetGroupId) => (form) => api.pours.reorderPourPresets(pourPresetGroupId, form),
  () => i18n._('Pour preset order updated.'),
);

export const useDeletePourPresetMutation = makeDeleteMutation<void, PourPresetDto>(
  POUR_PRESET_KEY,
  POUR_PRESETS_KEY,
  (api) => (pourPreset) => api.pours.deletePourPreset(pourPreset.entityId),
  (mutation) => i18n._('Pour preset').concat(` ${capitalize(mutation.name ?? '')} `).concat(i18n._('successfully deleted')),
  (mutation) => mutation.entityId,
  [POUR_PRESET_GROUPS_KEY],
);

export const useRestorePourPresetMutation = makeUpdateMutation<FileResponse, string>(
  POUR_PRESET_GROUP_KEY,
  POUR_PRESET_GROUPS_KEY,
  (api) => (pourPresetId) => api.pours.restorePourPreset(pourPresetId),
  () => i18n._('Pour preset successfully restored'),
  [POUR_PRESET_GROUPS_KEY],
);

export const useUpdatePourReferenceMutation = makeUpdateMutation<void, UpdatePourReferenceCommand>(
  POUR_KEY,
  POURS_KEY,
  (api, pourId) => (mutation) => api.pours.updatePourReference(pourId, mutation),
  () => i18n._('The pour reference has been successfully updated.'),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY],
);

export interface PublicPourParams {
  mineModelId: string;
  pourId: string;
  token: string;
}

export const useGetPublicPour = makeGetQuery<PublicPourApprovalViewDto, PublicPourParams>(
  PUBLIC_POUR_KEY,
  (api, params) => () => api.publicPours.get(params.pourId, params.mineModelId, params.token),
);

interface PublicApproveMutation {
  pourId: string;
  pourNoteId: string;
  comment: string;
  accessTokenHash: string;
}

export const usePublicApprovePourNoteApproval = makeUpdateMutation<void, PublicApproveMutation>(
  PUBLIC_POUR_KEY,
  null,
  (api) => (mutation) => api.publicPours.publicApprovePourNoteApproval(mutation.pourId, mutation.pourNoteId, mutation.accessTokenHash, '', null, mutation.comment),
  () => i18n._('The pour has been successfully approved.'),
);

export const usePourNoteReject = makeUpdateMutation<void, PublicApproveMutation>(
  PUBLIC_POUR_KEY,
  null,
  (api) => (mutation) => api.publicPours.rejectPourNote(mutation.pourId, mutation.pourNoteId, mutation.accessTokenHash, mutation.comment),
  () => i18n._('The pour has been successfully Rejected.'),
);

export const usePublicDownloadPourNote = makeGetQuery<FileResponse, { pourId: string, pourNoteId: string, sessionTokenHash: string }>(
  POUR_NOTE_KEY,
  (api, { pourId, pourNoteId, sessionTokenHash }) => () => api.publicPours.getPourNote(pourId, pourNoteId, sessionTokenHash),
);

export const useGetUcsStrengthGraphQuery = makeGetQuery<UscStrengthRecordDto[], { mineModelId: string; targetCuringDay: number }>(
  UCS_STRENGTH_GRAPH_KEY,
  (api, params) => () => api.breakingSchedules.getUcsStrengthGraphData(params.mineModelId, params.targetCuringDay),
);

interface GetRelevantStopePoursQueryParams extends PageParams {
  projectId: string
  scenarioId?: string
}

export const useGetRelevantStopePoursQuery = makeGetQuery<PagedListOfStopePoursDto, GetRelevantStopePoursQueryParams>(
  POURS_KEY,
  (api, params) => () => api.pours.getRelevantStopePours(params.projectId, params.page, params.pageSize, params.scenarioId),
);

// `scope` keeps this query's cache key distinct from useGetRelevantStopePoursQuery (which shares
// POURS_KEY and the same projectId/page/pageSize). The key still begins with POURS_KEY so every
// existing invalidateQueries('pours') call continues to refresh it.
export interface GetScheduledStopePoursQueryParams extends GetRelevantStopePoursQueryParams {
  scope: 'scheduled';
}

export const useGetScheduledStopePoursQuery = makeGetQuery<PagedListOfStopePoursDto, GetScheduledStopePoursQueryParams>(
  POURS_KEY,
  (api, params) => () => api.pours.getScheduledStopePours(params.projectId, params.page, params.pageSize, params.scenarioId),
);

export interface PourDocumentMutation {
  pourId: string;
  files: FileParameter[];
}

export interface RemovePourDocumentMutation {
  pourId: string;
  fileName: string;
}

export const useAddDocumentsToPourMutation = makeUpdateMutation<void, PourDocumentMutation>(
  POUR_KEY,
  POURS_KEY,
  (api) => (mutation) => api.pours.addDocumentToPour(mutation.pourId, mutation.files),
  () => i18n._('Documents added'),
);

export const useRemoveDocumentFromPourMutation = makeUpdateMutation<void, RemovePourDocumentMutation>(
  POUR_KEY,
  POURS_KEY,
  (api) => (mutation) => api.pours.removeDocumentFromPour(mutation.pourId, mutation.fileName),
  () => i18n._('Document removed'),
);

export const useDeletePourMutation = makeDeleteMutation<void, PourDto>(
  POUR_KEY,
  POURS_KEY,
  (api) => (pour) => api.pours.deletePour(pour.entityId),
  () => i18n._('Pour successfully deleted'),
  (mutation) => mutation.entityId,
);

export const useDeletePoursMutation = makeCreateMutation<FileResponse, BulkDeletePoursRequest>(
  POURS_KEY,
  (api) => (mutation) => api.pours.deletePours(mutation),
  () => i18n._('Pours successfully deleted'),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY],
);

export interface AutoCreatedPoursQueryParams {
  projectId: string;
  autoPourBatchId?: number;
  scenarioId?: string;
}

export const useAutoCreatedPoursQuery = makeListQuery<AutoCreatedPoursQueryParams, AutoCreatedPourDto[]>(
  POURS_KEY,
  (api, params) => () => api.pours.getAllAutoCreated(params.projectId, params.scenarioId),
);

export const useUndoAutoPoursMutation = makeDeleteMutation<FileResponse, AutoCreatedPoursQueryParams>(
  POUR_KEY,
  POURS_KEY,
  (api, scenarioId) => async (pour) => api.pours.undoAutoPours(pour.projectId, pour.autoPourBatchId as number, scenarioId ?? undefined),
  () => i18n._('Pours successfully deleted'),
  (mutation) => mutation.projectId,
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY],
);

export const useRestorePourMutation = makeCreateMutation<FileResponse, string>(
  POURS_KEY,
  (api) => (pourId) => api.pours.restorePour(pourId),
  () => i18n._('Pour successfully restored'),
  [STOPE_BACKFILLS_KEY, STOPE_BACKFILL_KEY, PAGED_POURS_KEY],
);
